import * as swcHelpers from '@swc/helpers';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Context } from '@grandvisionhq/carts-v2';
import { useLocation, useLink } from '@grandvisionhq/frk-routing';
import { useReplicateCart } from './use-replicate-cart.js';
import { updateUrlWithReload } from '../utils/update-url.js';
import { getUpdatedUrlWithQueryParams, appendQueryParamToUrl } from '../utils/get-updated-url-with-query-params.js';
import { PaymentFlow } from '../models/payment/types.js';

const useQrPaymentState = ({ paymentState , orderId: orderId1 , error , paymentResponse , paymentFlow , orderToken , onQrCodeRefresh ,  })=>{
    const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false);
    const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
    const [isQrRefreshLoading, setIsQrRefreshLoading] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [qrCodeExpireIn, setQrCodeExpireIn] = useState(0);
    const [externalUrl, setExternalUrl] = useState('');
    const { cart  } = Context.useCart();
    const { push  } = useLocation();
    const { href: orderConfirmHref  } = useLink({
        route: 'order.confirm',
        params: {
            cartId: '',
            orderData: ''
        }
    }) || {};
    const [replicateCart] = useReplicateCart();
    /**
   *  Reload checkout/payment with new cartId
   *
   */ const reloadPaymentPage = useCallback((cartId)=>{
        updateUrlWithReload(getUpdatedUrlWithQueryParams({
            cartId,
            type: paymentResponse.paymentMethod
        }));
    }, [
        paymentResponse.paymentMethod
    ]);
    /*
   *  When user cancels payment or payment fails
   *  actions: 1. Reset payment flow
   *           2. Replicate & restore cart
   *           3. Close QR modal
   */ const cancelPayment = useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderId) {
            const { cartId  } = yield replicateCart(orderId);
            cartId && reloadPaymentPage(cartId);
            setIsQrCodeModalOpen(false);
        });
        return function(orderId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        reloadPaymentPage,
        replicateCart
    ]);
    /*
   *  Listen for QR code payment status
   *  if paymentState is 'Paid' then call paymentSuccessOrder saga
   *  if PaymentState is 'Failed' or any error occurred, replicate and restore cart
   */ useEffect(()=>{
        if (paymentState === 'Paid' && orderConfirmHref) {
            push(appendQueryParamToUrl(orderConfirmHref, {
                cartId: cart === null || cart === void 0 ? void 0 : cart.id,
                orderData: orderToken
            }));
        }
        if (paymentState === 'Failed' || error) {
            cancelPayment(orderId1);
        }
    }, [
        paymentState,
        error,
        cancelPayment,
        orderId1,
        orderToken,
        cart === null || cart === void 0 ? void 0 : cart.id,
        orderConfirmHref,
        push
    ]);
    /*
   *  Reset QR code timeout
   *  Refresh QR code image
   */ useEffect(()=>{
        if (qrCodeExpireIn) {
            const timeoutId = setTimeout(()=>{
                setIsQrCodeExpired(true);
                setQrCodeExpireIn(0);
            }, qrCodeExpireIn);
            return ()=>clearTimeout(timeoutId)
            ;
        }
    }, [
        qrCodeExpireIn
    ]);
    /*
   *  Set/Update properties when QR code is updated
   */ useEffect(()=>{
        var ref, ref1, ref2;
        setQrCodeExpireIn(Number(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref = paymentResponse.resultDetails) === null || ref === void 0 ? void 0 : ref.qrCodeExpiresIn) * 1000);
        setQrCode(String(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref1 = paymentResponse.resultDetails) === null || ref1 === void 0 ? void 0 : ref1.qrCodeUrl));
        setExternalUrl(String(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref2 = paymentResponse.resultDetails) === null || ref2 === void 0 ? void 0 : ref2.url));
        setIsQrCodeExpired(false);
        setIsQrRefreshLoading(false);
    }, [
        paymentResponse
    ]);
    // Open QR code modal only when PaymentFlow is QR_Code
    useEffect(()=>{
        if (paymentFlow === PaymentFlow.QR_CODE) {
            setIsQrCodeModalOpen(true);
        }
    }, [
        paymentFlow
    ]);
    /*
   *  On refreshQr replicate cart
   *  Get latest QR code
   */ const handleRefreshQrCode = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (cart && orderId1) {
            setQrCodeExpireIn(0);
            setIsQrCodeExpired(true);
            const { cartId  } = yield replicateCart(orderId1);
            cartId && reloadPaymentPage(cartId);
            onQrCodeRefresh();
        }
        setIsQrRefreshLoading(true);
    }), [
        cart,
        onQrCodeRefresh,
        orderId1,
        reloadPaymentPage,
        replicateCart
    ]);
    return useMemo(()=>({
            externalUrl,
            isOpen: isQrCodeModalOpen,
            isQrCodeExpired,
            isQrRefreshLoading,
            onCancel: ()=>cancelPayment(orderId1)
            ,
            qrCodeUrl: qrCode,
            refreshQrCode: ()=>handleRefreshQrCode()
        })
    , [
        cancelPayment,
        externalUrl,
        handleRefreshQrCode,
        isQrCodeExpired,
        isQrCodeModalOpen,
        isQrRefreshLoading,
        orderId1,
        qrCode, 
    ]);
};

export { useQrPaymentState };
//# sourceMappingURL=use-qr-payment-state.js.map
